/**
 * ========================================
 * 날짜/시간 유틸리티 함수 (프론트엔드)
 * ========================================
 * Date and Time Utility Functions (Frontend)
 * - 언어 코드를 타임존 오프셋으로 변환
 * - 세션의 language를 이용한 날짜 포맷팅
 */

import { ref, computed, watch, getCurrentInstance, nextTick } from 'vue';
import { useSessionStore } from '@/shared/stores/sessionStore';
import { useLanguageStore } from '@/shared/stores/languageStore';
import { vuetify } from '@/shared/plugins/vuetify';
import { getVuetifyLocale } from '@/utils/locales/messages';

/**
 * 언어 코드를 타임존 오프셋으로 변환
 * @param {string} languageCode - 언어 코드 (ko, en, zh-cn, zh-tw, vn, ms)
 * @returns {string} 타임존 오프셋 (예: "+0900", "+0000", "+0800")
 * 
 * @example
 * getTimezoneFromLanguage('ko') // '+0900'
 * getTimezoneFromLanguage('en') // '+0000'
 * getTimezoneFromLanguage('zh-cn') // '+0800'
 */
export const getTimezoneFromLanguage = (languageCode: string): string => {
  if (!languageCode) {
    return '+0000'; // 기본값: UTC
  }

  const lang = languageCode.toLowerCase().trim();
  
  // 언어 코드 -> 타임존 오프셋 매핑
  const timezoneMap: Record<string, string> = {
    'ko': '+0900',      // 한국어 -> Asia/Seoul (GMT+09)
    'en': '+0000',      // 영어 -> UTC (GMT+00)
    'zh-cn': '+0800',   // 중국어(간체) -> Asia/Shanghai (GMT+08)
    'zh-tw': '+0800',   // 중국어(번체) -> Asia/Taipei (GMT+08)
    'vn': '+0700',      // 베트남어 -> Asia/Ho_Chi_Minh (GMT+07)
    'ms': '+0800',      // 말레이시아어 -> Asia/Kuala_Lumpur (GMT+08)
    'sg': '+0800'       // 싱가포르 -> Asia/Singapore (GMT+08)
  };

  return timezoneMap[lang] || '+0000'; // 기본값: UTC
};

/**
 * Jurisdiction 코드를 타임존 오프셋으로 변환
 * @param {string} jurisdiction - Jurisdiction 코드 (KO, SG, MY, VN, CN, HK 등)
 * @returns {string} 타임존 오프셋 (예: "+0900", "+0800", "+0700")
 * 
 * @example
 * getTimezoneFromJurisdiction('KP') // '+0900' (Korea)
 * getTimezoneFromJurisdiction('SG') // '+0800' (Singapore)
 * getTimezoneFromJurisdiction('VN') // '+0700' (Vietnam)
 */
export const getTimezoneFromJurisdiction = (jurisdiction: string): string => {
  if (!jurisdiction) {
    return '+0000'; // 기본값: UTC
  }

  const jd = jurisdiction.toUpperCase().trim();
  
  // Jurisdiction 코드 -> 타임존 오프셋 매핑
  const timezoneMap: Record<string, string> = {
    'KP': '+0900',      // Korea (North Korea) -> Asia/Pyongyang (GMT+09)
    'KR': '+0900',      // Korea (South Korea) -> Asia/Seoul (GMT+09)
    'SG': '+0800',      // Singapore -> Asia/Singapore (GMT+08)
    'MY': '+0800',      // Malaysia -> Asia/Kuala_Lumpur (GMT+08)
    'VN': '+0700',      // Vietnam -> Asia/Ho_Chi_Minh (GMT+07)
    'CN': '+0800',      // China -> Asia/Shanghai (GMT+08)
    'HK': '+0800',      // Hong Kong -> Asia/Hong_Kong (GMT+08)
    'TW': '+0800',      // Taiwan -> Asia/Taipei (GMT+08)
    'JP': '+0900',      // Japan -> Asia/Tokyo (GMT+09)
    'TH': '+0700',      // Thailand -> Asia/Bangkok (GMT+07)
    'ID': '+0700',      // Indonesia (Western) -> Asia/Jakarta (GMT+07)
    'PH': '+0800',      // Philippines -> Asia/Manila (GMT+08)
    'IN': '+0530',      // India -> Asia/Kolkata (GMT+05:30)
    'AU': '+1000',      // Australia (Eastern) -> Australia/Sydney (GMT+10)
    'GB': '+0000',      // United Kingdom -> Europe/London (GMT+00)
    'US': '-0500',      // United States (Eastern) -> America/New_York (GMT-05)
  };

  return timezoneMap[jd] || '+0000'; // 기본값: UTC
};

/**
 * 세션의 jurisdiction을 기준으로 현재 날짜를 타임존 적용하여 YYYY-MM-DD 형식으로 반환
 * @param {string} [jurisdiction] - Jurisdiction 코드 (선택, 제공되지 않으면 세션에서 가져옴)
 * @returns {string} YYYY-MM-DD 형식의 현재 날짜 문자열
 * 
 * @example
 * const today = getCurrentDateByJurisdiction(); // 세션의 jurisdiction 사용
 * const today = getCurrentDateByJurisdiction('KP'); // 특정 jurisdiction 지정
 */
export const getCurrentDateByJurisdiction = (jurisdiction?: string): string => {
  let jd: string;
  
  if (jurisdiction) {
    jd = jurisdiction;
  } else {
    // 세션에서 jurisdiction 가져오기
    try {
      const sessionStore = useSessionStore();
      const user = sessionStore.user;
      jd = user?.customer?.jurisdiction || '';
    } catch (error) {
      console.warn('Failed to get jurisdiction from session, using UTC:', error);
      jd = '';
    }
  }
  
  // 타임존 오프셋 가져오기
  const timezoneOffset = getTimezoneFromJurisdiction(jd);
  
  // 오프셋 파싱 (예: "+0900" -> +09:00)
  const sign = timezoneOffset.charAt(0); // '+' 또는 '-'
  const hours = parseInt(timezoneOffset.substring(1, 3), 10);
  const minutes = parseInt(timezoneOffset.substring(3, 5), 10);
  const offsetMinutes = (sign === '+' ? 1 : -1) * (hours * 60 + minutes);
  
  // UTC 시간을 기준으로 오프셋 적용
  const now = new Date();
  const utcTime = now.getTime() + (now.getTimezoneOffset() * 60000);
  const targetTime = new Date(utcTime + (offsetMinutes * 60000));
  
  // YYYY-MM-DD 형식으로 변환
  const year = targetTime.getFullYear();
  const month = String(targetTime.getMonth() + 1).padStart(2, '0');
  const day = String(targetTime.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

/**
 * 날짜 포맷 문자열을 실제 날짜 값으로 변환
 * @param {Date} date - 날짜 객체
 * @param {string} formatStr - 포맷 문자열 (예: "yyyy-mm-dd", "yyyy-MM-dd HH:mm:ss")
 * @returns {string} 포맷된 날짜 문자열
 */
const formatDateString = (date: Date, formatStr: string): string => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();

  let result = formatStr;

  // YYYY/yyyy -> 4자리 연도 (먼저 처리, 대소문자 모두 지원)
  result = result.replace(/YYYY/g, String(year));
  result = result.replace(/yyyy/g, String(year));
  // YY/yy -> 2자리 연도
  result = result.replace(/YY/g, String(year).slice(-2));
  result = result.replace(/yy/g, String(year).slice(-2));

  // MM -> 2자리 월 (01-12) (먼저 처리)
  result = result.replace(/MM/g, String(month).padStart(2, '0'));
  // M -> 월 (1-12) - 단일 M만 매칭 (MM 제외)
  result = result.replace(/\bM\b/g, String(month));

  // DD/dd -> 2자리 일 (01-31) (먼저 처리, 대소문자 모두 지원)
  result = result.replace(/DD/g, String(day).padStart(2, '0'));
  result = result.replace(/dd/g, String(day).padStart(2, '0'));
  // D/d -> 일 (1-31) - 단일 D/d만 매칭 (DD/dd 제외)
  result = result.replace(/\bD\b/g, String(day));
  result = result.replace(/\bd\b/g, String(day));

  // HH -> 2자리 시간 (00-23) (먼저 처리)
  result = result.replace(/HH/g, String(hours).padStart(2, '0'));
  // H -> 시간 (0-23) - 단일 H만 매칭 (HH 제외)
  result = result.replace(/\bH\b/g, String(hours));

  // mm -> 2자리 분 (00-59) (먼저 처리)
  result = result.replace(/mm/g, String(minutes).padStart(2, '0'));
  // m -> 분 (0-59) - 단일 m만 매칭 (mm 제외)
  result = result.replace(/\bm\b/g, String(minutes));

  // ss -> 2자리 초 (00-59) (먼저 처리)
  result = result.replace(/ss/g, String(seconds).padStart(2, '0'));
  // s -> 초 (0-59) - 단일 s만 매칭 (ss 제외)
  result = result.replace(/\bs\b/g, String(seconds));

  return result;
};

/**
 * 날짜/시간 데이터를 현재 언어 설정을 이용해 타임존 변환
 * @param {Date|string|number} dateData - 날짜 데이터 (Date 객체, ISO 문자열, 또는 타임스탬프)
 * @param {string} [format] - 반환 형식 
 *   - 'iso': ISO 형식 (기본값, "2024-01-01T12:00:00+09:00")
 *   - 'date': Date 객체 반환
 *   - 'string': YYYY-MM-DD HH:mm:ss 형식
 *   - 커스텀 포맷: "yyyy-mm-dd", "yyyy-MM-dd HH:mm:ss" 등 (대소문자 구분)
 * @param {string} [languageCode] - 언어 코드 (선택, 기본값: 현재 언어 스토어에서 가져옴)
 * @returns {string|Date} 타임존이 적용된 날짜 (format에 따라 다름)
 * 
 * @example
 * // ISO 형식으로 반환 (현재 언어 자동 추출)
 * const date = formatDateByLanguage(new Date());
 * // 결과: "2024-01-01T12:00:00+09:00"
 * 
 * // Date 객체로 반환
 * const date = formatDateByLanguage(new Date(), 'date');
 * 
 * // 문자열 형식으로 반환
 * const date = formatDateByLanguage(new Date(), 'string');
 * // 결과: "2024-01-01 12:00:00"
 * 
 * // 커스텀 포맷
 * const date = formatDateByLanguage(new Date(), 'yyyy-mm-dd');
 * // 결과: "2024-01-01"
 * 
 * const date = formatDateByLanguage(new Date(), 'yyyy-MM-dd HH:mm:ss');
 * // 결과: "2024-01-01 12:00:00"
 * 
 * // 특정 언어 지정
 * const date = formatDateByLanguage(new Date(), 'yyyy-mm-dd', 'ko');
 */
export const formatDateByLanguage = (
  dateData: Date | string | number,
  format: string = 'iso',
  languageCode?: string
): string | Date => {
  // 날짜 데이터 파싱
  let date: Date;
  if (dateData instanceof Date) {
    date = new Date(dateData);
  } else if (typeof dateData === 'string') {
    date = new Date(dateData);
  } else if (typeof dateData === 'number') {
    date = new Date(dateData);
  } else {
    throw new Error('Invalid date data. Expected Date, string, or number.');
  }

  // 유효한 날짜인지 확인
  if (isNaN(date.getTime())) {
    throw new Error('Invalid date. Cannot parse date data.');
  }

  // 언어 코드 가져오기 (매개변수 또는 세션에서)
  let lang: string;
  if (languageCode) {
    lang = languageCode;
  } else {
    try {
      // 세션 스토어에서 user의 language 가져오기
      const sessionStore = useSessionStore();
      const user = sessionStore.user;
      
      // 세션의 user.user.language에서 가져오기 (user.user.language가 실제 언어 속성)
      lang = (user?.user?.language || 'en').toLowerCase();
    } catch (error) {
      // 세션 스토어를 사용할 수 없는 경우 기본값 사용
      lang = 'en';
    }
  }

  // 언어 코드를 타임존 오프셋으로 변환
  const timezoneOffset = getTimezoneFromLanguage(lang);

  // 오프셋 파싱 (예: "+0900" -> +09:00)
  const sign = timezoneOffset.charAt(0); // '+' 또는 '-'
  const hours = parseInt(timezoneOffset.substring(1, 3), 10);
  const minutes = parseInt(timezoneOffset.substring(3, 5), 10);
  const offsetMinutes = (sign === '+' ? 1 : -1) * (hours * 60 + minutes);

  // UTC 시간을 기준으로 오프셋 적용
  const utcTime = date.getTime() + (date.getTimezoneOffset() * 60000);
  const targetTime = new Date(utcTime + (offsetMinutes * 60000));

  // 포맷에 따라 처리
  if (format === 'date') {
    return targetTime;
  } else if (format === 'iso') {
    // ISO 형식 (기본값)
    const year = targetTime.getFullYear();
    const month = String(targetTime.getMonth() + 1).padStart(2, '0');
    const day = String(targetTime.getDate()).padStart(2, '0');
    const hrs = String(targetTime.getHours()).padStart(2, '0');
    const mins = String(targetTime.getMinutes()).padStart(2, '0');
    const secs = String(targetTime.getSeconds()).padStart(2, '0');
    const offsetStr = `${sign}${String(Math.abs(hours)).padStart(2, '0')}:${String(Math.abs(minutes)).padStart(2, '0')}`;
    return `${year}-${month}-${day}T${hrs}:${mins}:${secs}${offsetStr}`;
  } else if (format === 'string') {
    // YYYY-MM-DD HH:mm:ss 형식
    const year = targetTime.getFullYear();
    const month = String(targetTime.getMonth() + 1).padStart(2, '0');
    const day = String(targetTime.getDate()).padStart(2, '0');
    const hrs = String(targetTime.getHours()).padStart(2, '0');
    const mins = String(targetTime.getMinutes()).padStart(2, '0');
    const secs = String(targetTime.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hrs}:${mins}:${secs}`;
  } else {
    // 커스텀 포맷 문자열 처리
    return formatDateString(targetTime, format);
  }
};

/**
 * 날짜/시간 데이터를 현재 언어 설정을 이용해 타임존 변환 (Composable 버전)
 * Vue 컴포넌트에서 사용하기 편리한 composable 함수
 * 세션의 language를 자동으로 사용합니다.
 * 
 * @example
 * ```vue
 * <script setup lang="ts">
 * import { useFormatDate } from '@/utils/dateUtils';
 * 
 * const formatDate = useFormatDate();
 * 
 * const formattedDate = formatDate(new Date(), 'yyyy-mm-dd');
 * </script>
 * ```
 */
export const useFormatDate = () => {
  const sessionStore = useSessionStore();
  
  return (dateData: Date | string | number, format: string = 'iso', languageCode?: string) => {
    // 언어 코드가 제공되지 않으면 세션에서 가져오기
    let lang: string;
    if (languageCode) {
      lang = languageCode;
    } else {
      const user = sessionStore.user;
      lang = (user?.user?.language || 'en').toLowerCase();
    }
    return formatDateByLanguage(dateData, format, lang);
  };
};

/**
 * 세션에 저장된 사용자의 dateFormat을 사용하여 날짜 포맷팅
 * Jurisdiction 시간대 기준으로 변환 후 포맷팅
 *
 * @deprecated formatDocDate 또는 formatDateByJurisdiction 사용 권장
 * @param {Date|string|number} dateData - 날짜 데이터 (Date 객체, ISO 문자열, 또는 타임스탬프)
 * @param {string} [fallbackFormat] - 세션에 dateFormat이 없을 경우 사용할 기본 포맷 (기본값: 'DD MMM YYYY')
 * @returns {string} 포맷된 날짜 문자열
 *
 * @example
 * const formatted = formatDateByUserFormat(new Date());
 * // 결과: "15 Jan. 2024" (세션의 dateFormat 및 jurisdiction 시간대 적용)
 */
export const formatDateByUserFormat = (
  dateData: Date | string | number,
  fallbackFormat: string = 'DD MMM YYYY'
): string => {
  // formatDocDate와 동일한 로직 사용 (Jurisdiction 시간대 + dateFormat)
  return formatDocDate(dateData, fallbackFormat);
};

/**
 * 세션에 저장된 사용자의 dateFormat을 사용하여 날짜 포맷팅 (Composable 버전)
 * Jurisdiction 시간대 기준으로 변환 후 포맷팅
 *
 * @deprecated useFormatDocDate 사용 권장
 * @example
 * ```vue
 * <script setup lang="ts">
 * import { useFormatDateByUserFormat } from '@/shared/composables/dateUtils';
 *
 * const formatDate = useFormatDateByUserFormat();
 *
 * const formattedDate = formatDate(new Date());
 * // 세션의 dateFormat 및 jurisdiction 시간대 적용
 * </script>
 * ```
 */
export const useFormatDateByUserFormat = () => {
  return (dateData: Date | string | number, fallbackFormat: string = 'DD MMM YYYY'): string => {
    return formatDocDate(dateData, fallbackFormat);
  };
};

const TEXT_MONTH_MAP: Record<string, number> = {
  'jan': 1, 'january': 1, 'feb': 2, 'february': 2, 'mar': 3, 'march': 3,
  'apr': 4, 'april': 4, 'may': 5, 'jun': 6, 'june': 6,
  'jul': 7, 'july': 7, 'aug': 8, 'august': 8, 'sep': 9, 'september': 9,
  'oct': 10, 'october': 10, 'nov': 11, 'november': 11, 'dec': 12, 'december': 12,
};

/** 텍스트 월 포함 날짜 문자열 유연하게 파싱 (직접 입력 지원) */
export const parseFlexibleDate = (dateString: string): string => {
  if (!dateString) return '';
  const s = dateString.trim();
  // ISO: 2026-05-20
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) return s;
  // D/DD MMMM YYYY or D/DD MMM YYYY: "20 May 2026", "20 May. 2026"
  const dmy = s.match(/^(\d{1,2})\s+([A-Za-z]+\.?)\s+(\d{4})$/);
  if (dmy) {
    const day = dmy[1].padStart(2, '0');
    const monthKey = dmy[2].replace('.', '').toLowerCase();
    const month = TEXT_MONTH_MAP[monthKey];
    if (month) return `${dmy[3]}-${String(month).padStart(2, '0')}-${day}`;
  }
  // MMMM D, YYYY or MMM D, YYYY: "May 20, 2026"
  const mdy = s.match(/^([A-Za-z]+\.?)\s+(\d{1,2}),?\s+(\d{4})$/);
  if (mdy) {
    const monthKey = mdy[1].replace('.', '').toLowerCase();
    const month = TEXT_MONTH_MAP[monthKey];
    const day = mdy[2].padStart(2, '0');
    if (month) return `${mdy[3]}-${String(month).padStart(2, '0')}-${day}`;
  }
  return '';
};

/**
 * 사용자 입력 날짜 문자열을 dateFormat에 맞춰 파싱하여 YYYY-MM-DD 형식으로 변환
 * @param {string} dateString - 사용자가 입력한 날짜 문자열
 * @param {string} dateFormat - 날짜 포맷 (예: 'MM/DD/YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD')
 * @returns {string} YYYY-MM-DD 형식의 날짜 문자열 (파싱 실패 시 빈 문자열)
 */
const parseDateString = (dateString: string, dateFormat: string): string => {
  if (!dateString || !dateFormat) return '';
  
  const format = dateFormat.toUpperCase();
  let year = '', month = '', day = '';
  
  if (format.includes('/')) {
    const parts = dateString.split('/');
    if (format.startsWith('MM')) {
      // MM/DD/YYYY
      month = parts[0];
      day = parts[1];
      year = parts[2];
    } else if (format.startsWith('DD')) {
      // DD/MM/YYYY
      day = parts[0];
      month = parts[1];
      year = parts[2];
    } else if (format.startsWith('YYYY')) {
      // YYYY/MM/DD
      year = parts[0];
      month = parts[1];
      day = parts[2];
    }
  } else if (format.includes('-')) {
    const parts = dateString.split('-');
    if (format.startsWith('YYYY')) {
      // YYYY-MM-DD
      year = parts[0];
      month = parts[1];
      day = parts[2];
    } else if (format.startsWith('DD')) {
      // DD-MM-YYYY
      day = parts[0];
      month = parts[1];
      year = parts[2];
    }
  } else if (format.includes('.')) {
    const parts = dateString.split('.');
    if (format.startsWith('YYYY')) {
      // YYYY.MM.DD
      year = parts[0];
      month = parts[1];
      day = parts[2];
    } else if (format.startsWith('MM')) {
      // MM.DD.YYYY
      month = parts[0];
      day = parts[1];
      year = parts[2];
    } else if (format.startsWith('DD')) {
      // DD.MM.YYYY
      day = parts[0];
      month = parts[1];
      year = parts[2];
    }
  }
  
  if (year && month && day) {
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  
  return '';
};

/**
 * 다양한 입력 형식을 내부 저장용 YYYY-MM-DD로 정규화.
 * MY Register API(SQL style 103: dd/mm/yyyy) 등 JS Date가 MM/DD로 오해하는 문자열을 처리한다.
 */
export const normalizeDateInputToIso = (
  value: string | null | undefined,
  dateFormat?: string,
): string => {
  if (value == null) return '';
  const s = String(value).trim();
  if (!s) return '';

  const isoMatch = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (isoMatch) {
    const [, y, m, d] = isoMatch;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }

  // SQL Server style 103 / MY register: dd/mm/yyyy
  const dmySlash = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmySlash) {
    const [, d, m, y] = dmySlash;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }

  const yyyyDot = s.match(/^(\d{4})\.(\d{1,2})\.(\d{1,2})$/);
  if (yyyyDot) {
    const [, y, m, d] = yyyyDot;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }

  const dmyDot = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (dmyDot) {
    const [, d, m, y] = dmyDot;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }

  if (dateFormat) {
    const parsed = parseDateString(s, dateFormat);
    if (parsed) return parsed;
  }

  const flex = parseFlexibleDate(s);
  if (flex) return flex;

  return s;
};

/**
 * 날짜 필드용 공통 Composable
 * 세션의 dateFormat과 다국어 처리를 지원하는 날짜 필드 기능을 제공합니다.
 * 
 * @returns {object} 날짜 필드 관련 computed, ref, 함수들
 * 
 * @example
 * ```vue
 * <script setup lang="ts">
 * import { useDateField } from '@/shared/composables/dateUtils';
 * 
 * const {
 *   dateValue,           // 내부 저장용: YYYY-MM-DD 형식 (ref<string>)
 *   dateDisplay,         // 표시용: 사용자 dateFormat에 맞춘 형식 (computed)
 *   datePickerValue,     // v-date-picker용: Date | null (computed)
 *   datePlaceholder,     // placeholder 텍스트 (computed)
 *   datePickerLocale,    // Vuetify locale (computed)
 *   firstDayOfWeek,      // 주의 시작 요일 (computed)
 *   dateMenu             // v-menu의 modelValue (ref<boolean>)
 * } = useDateField();
 * </script>
 * 
 * <template>
 *   <v-menu v-model="dateMenu" :close-on-content-click="false" location="bottom">
 *     <template v-slot:activator="{ props }">
 *       <v-text-field
 *         v-bind="props"
 *         v-model="dateDisplay"
 *         :placeholder="datePlaceholder"
 *         readonly
 *         prepend-inner-icon="mdi-calendar"
 *       />
 *     </template>
 *     <v-date-picker
 *       v-model="datePickerValue"
 *       :first-day-of-week="firstDayOfWeek"
 *       @update:model-value="dateMenu = false"
 *     />
 *   </v-menu>
 * </template>
 * ```
 */
export const useDateField = (initialValue?: string | null) => {
  const sessionStore = useSessionStore();
  const languageStore = useLanguageStore();
  const instance = getCurrentInstance();
  const formatDateByUserFormat = useFormatDateByUserFormat();
  
  // 내부 저장용: YYYY-MM-DD 형식
  // initialValue가 제공되지 않으면 빈 문자열로 초기화 (오늘 날짜로 자동 바인딩 안 함)
  const getInitialDateValue = (): string => {
    if (initialValue !== undefined && initialValue !== null && initialValue !== '') {
      const fmt = sessionStore.user?.user?.dateFormat || 'dd/MM/yyyy';
      return normalizeDateInputToIso(initialValue, fmt);
    }
    return '';
  };
  
  const dateValue = ref<string>(getInitialDateValue());
  
  // v-menu의 modelValue
  const dateMenu = ref(false);
  
  // 사용자 세션의 dateFormat 가져오기
  const userDateFormat = computed(() => {
    return sessionStore.user?.user?.dateFormat || 'dd/MM/yyyy';
  });
  
  // 언어 코드를 Vuetify date-picker locale 형식으로 변환
  const datePickerLocale = computed(() => {
    const currentLang = languageStore.getCurrentLanguage();
    return getVuetifyLocale(currentLang);
  });
  
  // 주의 시작 요일 (한국, 중국, 베트남, 말레이시아는 월요일, 영어권은 일요일)
  const firstDayOfWeek = computed(() => {
    const currentLang = languageStore.getCurrentLanguage();
    // 월요일 시작: ko, zh-cn, zh-tw, vn, ms
    const mondayStart = ['ko', 'zh-cn', 'zh-tw', 'vn', 'ms'];
    return mondayStart.includes(currentLang) ? 1 : 0; // 0 = 일요일, 1 = 월요일
  });
  
  // Vuetify locale 동적 변경
  watch(() => datePickerLocale.value, (newLocale) => {
    try {
      // 전역 vuetify 인스턴스에 직접 접근
      if (vuetify && vuetify.locale && newLocale) {
        // Vuetify 3의 locale 변경 (타입 단언 사용)
        const localeInstance = vuetify.locale as any;
        if (localeInstance.current) {
          localeInstance.current.value = newLocale;
        } else if (localeInstance.locale) {
          localeInstance.locale.value = newLocale;
        } else if (localeInstance.name) {
          // name 속성을 직접 변경
          localeInstance.name.value = newLocale;
        }
      }
      
      // fallback: getCurrentInstance를 통한 접근
      if (instance && instance.appContext && instance.appContext.config) {
        const vuetifyInstance = instance.appContext.config.globalProperties.$vuetify;
        if (vuetifyInstance && vuetifyInstance.locale && newLocale) {
          const localeInstance = vuetifyInstance.locale as any;
          if (localeInstance.current) {
            localeInstance.current.value = newLocale;
          } else if (localeInstance.locale) {
            localeInstance.locale.value = newLocale;
          } else if (localeInstance.name) {
            localeInstance.name.value = newLocale;
          }
        }
      }
    } catch (error) {
      console.warn('Failed to update Vuetify locale:', error);
    }
  }, { immediate: true });
  
  // Placeholder 생성 (사용자 dateFormat에 맞춤)
  const datePlaceholder = computed(() => {
    // 세션 포맷을 그대로 "placeholder 토큰"으로 정규화해서 보여준다.
    // 예) "dd MMM yyyy" -> "DD MMM YYYY", "d/M/yyyy" -> "D/M/YYYY"
    const raw = String(userDateFormat.value || '').trim();
    if (!raw) return 'YYYY-MM-DD';

    // 이미 YYYY/MM/DD 류로 저장된 케이스도 지원하고,
    // MMM(월 이름) 같이 구분자가 공백인 포맷도 그대로 보존한다.
    return raw
      // year
      .replace(/y{4}/gi, 'YYYY')
      .replace(/y{2}/gi, 'YY')
      // month (MMMM/MMM/MM/M)
      .replace(/m{4}/gi, 'MMMM')
      .replace(/m{3}/gi, 'MMM')
      .replace(/m{2}/gi, 'MM')
      // day (DD/D)
      .replace(/d{2}/gi, 'DD')
      .replace(/\bd\b/gi, 'D');
  });
  
  // v-date-picker용 Date 객체 (Date | null)
  const datePickerValue = computed({
    get: () => {
      if (!dateValue.value) return null;
      const iso = normalizeDateInputToIso(dateValue.value, userDateFormat.value);
      const parts = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (parts) {
        const [, y, m, d] = parts;
        return new Date(Number(y), Number(m) - 1, Number(d));
      }
      try {
        const date = new Date(dateValue.value);
        if (isNaN(date.getTime())) return null;
        return date;
      } catch {
        return null;
      }
    },
    set: (val: Date | null) => {
      if (!val) {
        dateValue.value = '';
        return;
      }
      // Date 객체를 YYYY-MM-DD 형식으로 변환
      const year = val.getFullYear();
      const month = String(val.getMonth() + 1).padStart(2, '0');
      const day = String(val.getDate()).padStart(2, '0');
      dateValue.value = `${year}-${month}-${day}`;
    }
  });
  
  // 날짜 표시용 (dateUtils의 formatDateByUserFormat 사용)
  const dateDisplay = computed({
    get: () => {
      if (!dateValue.value) return '';
      const iso = normalizeDateInputToIso(dateValue.value, userDateFormat.value);
      const parts = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (parts) {
        const [, y, m, d] = parts;
        return formatDateByUserFormat(new Date(Number(y), Number(m) - 1, Number(d)), userDateFormat.value);
      }
      try {
        const date = new Date(dateValue.value);
        if (isNaN(date.getTime())) return '';
        return formatDateByUserFormat(date, userDateFormat.value);
      } catch {
        return '';
      }
    },
    set: (val: string) => {
      if (!val) {
        dateValue.value = '';
        return;
      }
      // 1) 세션 포맷 기반 파싱 시도
      const parsed = parseDateString(val, userDateFormat.value);
      if (parsed) { dateValue.value = parsed; return; }
      // 2) 텍스트 월 포함 유연한 파싱 (직접 입력 지원)
      const flex = parseFlexibleDate(val);
      if (flex) { dateValue.value = flex; }
    }
  });
  
  watch(dateValue, (val) => {
    if (!val) return;
    const normalized = normalizeDateInputToIso(val, userDateFormat.value);
    if (normalized !== val && /^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
      dateValue.value = normalized;
    }
  }, { immediate: true });

  // v-date-picker의 "오늘" 하이라이트를 jurisdiction 기준으로 설정
  const todayDate = computed(() => {
    return getCurrentDateByJurisdiction();
  });

  // v-date-picker에 공통으로 적용할 props
  // 사용법: <v-date-picker v-bind="datePickerProps" v-model="datePickerValue" />
  const datePickerProps = computed(() => {
    // show-current에 Date 객체 전달 (Vuetify가 Date 객체를 기대할 수 있음)
    const todayStr = todayDate.value;
    let showCurrentDate: Date | string = todayStr;

    if (todayStr) {
      const [year, month, day] = todayStr.split('-').map(Number);
      showCurrentDate = new Date(year, month - 1, day, 12, 0, 0);
    }

    return {
      'show-current': showCurrentDate,
      'first-day-of-week': firstDayOfWeek.value
    };
  });

  // ── 직접 입력 지원: localText 버퍼 ──
  // blur 시에만 파싱하고, 성공해도 사용자가 타이핑한 텍스트를 그대로 유지.
  // (세션 포맷으로 재변환하지 않음)
  const localText = ref('');
  let preventLocalTextUpdate = false; // blur 커밋 후 watcher가 덮어쓰는 것을 막는 플래그

  watch(dateDisplay, (v) => {
    if (preventLocalTextUpdate) { preventLocalTextUpdate = false; return; }
    localText.value = v; // 달력 선택 / 외부 prefill 시에만 포맷 동기화
  }, { immediate: true });

  function onDateTextBlur(text?: string) {
    const t = (text ?? localText.value).trim();
    if (!t) { dateValue.value = ''; return; }
    // 포맷 무관 유연한 파싱: 세션 포맷 → 텍스트 월 → JS Date fallback
    const parsed =
      parseDateString(t, userDateFormat.value) ||
      parseFlexibleDate(t) ||
      (() => {
        try {
          const d = new Date(t);
          if (!isNaN(d.getTime())) {
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${y}-${m}-${day}`;
          }
        } catch { /* ignore */ }
        return '';
      })();
    if (parsed) {
      preventLocalTextUpdate = true; // watcher가 localText를 덮어쓰지 않도록
      dateValue.value = parsed;
      // dateValue가 이전과 같으면 watcher가 발화하지 않아 플래그가 남을 수 있음
      // → nextTick으로 보장: 달력 선택은 항상 세션 포맷으로 표시
      nextTick(() => { preventLocalTextUpdate = false; });
    } else {
      localText.value = dateDisplay.value; // 파싱 불가 → 마지막 유효값으로 되돌림
    }
  }

  return {
    dateValue,
    dateDisplay,
    datePickerValue,
    datePlaceholder,
    datePickerLocale,
    firstDayOfWeek,
    dateMenu,
    userDateFormat,
    todayDate, // v-date-picker의 show-current prop에 사용 (개별 사용 시)
    datePickerProps, // v-date-picker 공통 props (v-bind로 사용)
    localText,
    onDateTextBlur,
  };
};

/**
 * ========================================
 * Document Date Format (dateFormat) 관련 함수
 * ========================================
 * 세션의 dateFormat을 사용하여 날짜를 "10 Dec 2025" 형식으로 변환
 * Jurisdiction 시간대 기준으로 변환
 * 기본 포맷: "DD MMM YYYY"
 */

// 월 이름 매핑 (영어 - 3글자 약어 + 점, May는 줄임이 아니므로 점 없음)
const MONTH_NAMES_EN = ['Jan.', 'Feb.', 'Mar.', 'Apr.', 'May', 'Jun.', 'Jul.', 'Aug.', 'Sep.', 'Oct.', 'Nov.', 'Dec.'];

// 월 이름 매핑 (영어 - 전체)
const MONTH_NAMES_FULL_EN = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/**
 * Jurisdiction을 Timezone으로 매핑
 */
const jurisdictionTimezoneMap: Record<string, string> = {
  'SG': 'Asia/Singapore',      // 싱가포르 UTC+8
  'MY': 'Asia/Kuala_Lumpur',   // 말레이시아 UTC+8
  'VN': 'Asia/Ho_Chi_Minh',    // 베트남 UTC+7
  'US': 'America/New_York',    // 미국 (동부) UTC-5/-4
  'CN': 'Asia/Shanghai',       // 중국 UTC+8
  'HK': 'Asia/Hong_Kong',      // 홍콩 UTC+8
  'TW': 'Asia/Taipei',         // 대만 UTC+8
  'KR': 'Asia/Seoul',          // 한국 UTC+9
  'JP': 'Asia/Tokyo',          // 일본 UTC+9
  'TH': 'Asia/Bangkok',        // 태국 UTC+7
  'ID': 'Asia/Jakarta',        // 인도네시아 UTC+7
  'PH': 'Asia/Manila',         // 필리핀 UTC+8
  'IN': 'Asia/Kolkata',        // 인도 UTC+5:30
  'AU': 'Australia/Sydney',    // 호주 (시드니) UTC+10/+11
  'GB': 'Europe/London',       // 영국 UTC+0/+1
};

/**
 * Jurisdiction 기반 Timezone 이름 가져오기 (예: 'Asia/Singapore')
 * @param {string} [jurisdiction] - Jurisdiction 코드 (예: 'SG', 'KR')
 * @returns {string} IANA Timezone 이름
 */
export const getTimezoneByJurisdiction = (jurisdiction?: string | null): string => {
  if (jurisdiction) {
    const tz = jurisdictionTimezoneMap[jurisdiction.toUpperCase()];
    if (tz) return tz;
  }
  // 기본값: UTC
  return 'UTC';
};

/**
 * UTC 날짜를 Jurisdiction 시간대로 변환 (시간 포함)
 */
const convertToJurisdictionTimeWithTime = (dateValue: string, jurisdiction?: string | null): {
  year: number;
  month: number; // 0-11
  day: number;
  hours: string;
  minutes: string;
  seconds: string;
} | null => {
  try {
    // DB에서 받은 날짜 문자열을 UTC로 인식하도록 처리
    let normalizedDateValue = dateValue.trim();

    // 공백을 'T'로 변환 (ISO 8601 형식)
    normalizedDateValue = normalizedDateValue.replace(' ', 'T');

    // 타임존 표시가 없으면 'Z' (UTC) 추가
    if (!normalizedDateValue.endsWith('Z') &&
        !normalizedDateValue.includes('+') &&
        !normalizedDateValue.match(/[-]\d{2}:\d{2}$/)) {
      normalizedDateValue += 'Z';
    }

    const date = new Date(normalizedDateValue);
    if (Number.isNaN(date.getTime())) return null;

    const timezone = getTimezoneByJurisdiction(jurisdiction);

    // Intl.DateTimeFormat을 사용하여 특정 시간대로 변환
    const options: Intl.DateTimeFormatOptions = {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    };

    // 각 부분을 개별적으로 추출
    const formatter = new Intl.DateTimeFormat('en-CA', options);
    const parts = formatter.formatToParts(date);

    const getPart = (type: string): string => {
      const part = parts.find(p => p.type === type);
      return part?.value || '00';
    };

    return {
      year: parseInt(getPart('year'), 10),
      month: parseInt(getPart('month'), 10) - 1, // 0-11로 변환
      day: parseInt(getPart('day'), 10),
      hours: getPart('hour'),
      minutes: getPart('minute'),
      seconds: getPart('second')
    };
  } catch (error) {
    console.error('Error converting to jurisdiction time with time:', error);
    return null;
  }
};

/**
 * UTC 날짜를 Jurisdiction 시간대로 변환
 */
const convertToJurisdictionTime = (dateValue: string, jurisdiction?: string | null): {
  year: number;
  month: number; // 0-11
  day: number;
} | null => {
  try {
    // DB에서 받은 날짜 문자열을 UTC로 인식하도록 처리
    let normalizedDateValue = dateValue.trim();

    // 공백을 'T'로 변환 (ISO 8601 형식)
    normalizedDateValue = normalizedDateValue.replace(' ', 'T');

    // 타임존 표시가 없으면 'Z' (UTC) 추가
    if (!normalizedDateValue.endsWith('Z') &&
        !normalizedDateValue.includes('+') &&
        !normalizedDateValue.match(/[-]\d{2}:\d{2}$/)) {
      normalizedDateValue += 'Z';
    }

    const date = new Date(normalizedDateValue);
    if (Number.isNaN(date.getTime())) return null;

    const timezone = getTimezoneByJurisdiction(jurisdiction);

    // Intl.DateTimeFormat을 사용하여 특정 시간대로 변환
    const options: Intl.DateTimeFormatOptions = {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour12: false
    };

    // 각 부분을 개별적으로 추출
    const formatter = new Intl.DateTimeFormat('en-CA', options);
    const parts = formatter.formatToParts(date);

    const getPart = (type: string): string => {
      const part = parts.find(p => p.type === type);
      return part?.value || '00';
    };

    return {
      year: parseInt(getPart('year'), 10),
      month: parseInt(getPart('month'), 10) - 1, // 0-11로 변환
      day: parseInt(getPart('day'), 10)
    };
  } catch (error) {
    console.error('Error converting to jurisdiction time:', error);
    return null;
  }
};

/**
 * docDateFormat 문자열을 파싱하여 날짜 문자열로 변환
 * @param {Date} date - 날짜 객체
 * @param {string} formatStr - 포맷 문자열 (예: "DD MMM YYYY", "dd MMM yyyy", "d MMMM yyyy")
 * @returns {string} 포맷된 날짜 문자열
 *
 * 지원 포맷 토큰 (대소문자 모두 지원):
 * - DD/dd: 2자리 일 (01-31)
 * - D/d: 일 (1-31, 앞에 0 없음)
 * - MMM: 월 약어 (Jan., Feb., ... / May는 점 없음)
 * - MMMM: 월 전체 (January, February, ...)
 * - MM: 2자리 월 (01-12)
 * - YYYY/yyyy: 4자리 연도
 * - YY/yy: 2자리 연도
 */
const parseDocDateFormat = (date: Date, formatStr: string): string => {
  const year = date.getFullYear();
  const month = date.getMonth(); // 0-11
  const day = date.getDate();

  let result = formatStr;

  // 순서 중요: 긴 토큰 먼저 처리 (MMMM -> MMM -> MM, yyyy -> yy)
  // yyyy/YYYY -> 4자리 연도
  result = result.replace(/yyyy/g, String(year));
  result = result.replace(/YYYY/g, String(year));
  // yy/YY -> 2자리 연도
  result = result.replace(/yy/g, String(year).slice(-2));
  result = result.replace(/YY/g, String(year).slice(-2));
  // MMMM -> 월 전체 이름
  result = result.replace(/MMMM/g, MONTH_NAMES_FULL_EN[month]);
  // MMM -> 월 약어 (대문자만, 소문자 mm은 분을 의미할 수 있으므로 제외)
  result = result.replace(/MMM/g, MONTH_NAMES_EN[month]);
  // MM -> 2자리 월 (대문자만)
  result = result.replace(/MM/g, String(month + 1).padStart(2, '0'));
  // dd/DD -> 2자리 일 (먼저 처리)
  result = result.replace(/dd/g, String(day).padStart(2, '0'));
  result = result.replace(/DD/g, String(day).padStart(2, '0'));
  // d/D -> 일 (앞에 0 없음) - dd/DD가 아닌 단독 d/D만 매칭
  result = result.replace(/\bd\b/g, String(day));
  result = result.replace(/\bD\b/g, String(day));

  return result;
};

/**
 * 세션의 dateFormat을 사용하여 날짜 포맷팅
 * Jurisdiction 시간대 기준으로 변환 후 포맷팅
 * @param {Date|string|number} dateData - 날짜 데이터 (Date 객체, ISO 문자열, YYYY-MM-DD 문자열, 또는 타임스탬프)
 * @param {string} [fallbackFormat] - 세션에 dateFormat이 없을 경우 사용할 기본 포맷 (기본값: 'DD MMM YYYY')
 * @returns {string} 포맷된 날짜 문자열 (예: "10 Dec 2025")
 *
 * @example
 * // 세션의 dateFormat 사용 (예: 'DD MMM YYYY')
 * const formatted = formatDocDate('2025-12-10');
 * // 결과: "10 Dec 2025"
 *
 * // 세션에 dateFormat이 없을 경우 기본값 사용
 * const formatted = formatDocDate(new Date(), 'MMM DD, YYYY');
 * // 결과: "Dec 10, 2025"
 */
export const formatDocDate = (
  dateData: Date | string | number,
  fallbackFormat: string = 'DD MMM YYYY'
): string => {
  if (!dateData) return '';

  // 세션에서 dateFormat 및 jurisdiction 가져오기
  let dateFormat: string = fallbackFormat;
  let jurisdiction: string | null = null;
  try {
    const sessionStore = useSessionStore();
    const user = sessionStore.user;
    // 세션의 dateFormat 사용, 없으면 기본값 사용
    dateFormat = user?.user?.dateFormat || fallbackFormat;
    // 세션의 jurisdiction 가져오기
    jurisdiction = user?.customer?.jurisdiction || null;
  } catch (error) {
    // 세션 스토어를 사용할 수 없는 경우 기본값 사용
    console.warn('Failed to get dateFormat/jurisdiction from session, using fallback:', error);
  }

  // 날짜 문자열인 경우 Jurisdiction 시간대로 변환
  if (typeof dateData === 'string') {
    const converted = convertToJurisdictionTime(dateData, jurisdiction);
    if (converted) {
      // 변환된 시간 기준으로 Date 객체 생성 (로컬 시간으로)
      const date = new Date(converted.year, converted.month, converted.day);
      return parseDocDateFormat(date, dateFormat);
    }
    // 변환 실패 시 기존 로직으로 fallback
  }

  // Date 또는 number 타입의 경우 기존 로직 사용
  let date: Date;
  if (dateData instanceof Date) {
    date = new Date(dateData);
  } else if (typeof dateData === 'string') {
    // YYYY-MM-DD 형식 확인
    const dateMatch = dateData.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dateMatch) {
      const [, yearStr, monthStr, dayStr] = dateMatch;
      // 로컬 타임존으로 Date 생성 (UTC 오프셋 문제 방지)
      date = new Date(parseInt(yearStr), parseInt(monthStr) - 1, parseInt(dayStr));
    } else {
      date = new Date(dateData);
    }
  } else if (typeof dateData === 'number') {
    date = new Date(dateData);
  } else {
    return '';
  }

  // 유효한 날짜인지 확인
  if (isNaN(date.getTime())) {
    return '';
  }

  return parseDocDateFormat(date, dateFormat);
};

/**
 * 세션의 dateFormat만 사용하여 날짜 포맷팅 (Jurisdiction 시간대 변환 없음)
 * 설립일, 생년월일 등 시간대 변환이 필요 없는 날짜에 사용
 * @param {Date|string|number} dateData - 날짜 데이터 (Date 객체, ISO 문자열, YYYY-MM-DD 문자열, 또는 타임스탬프)
 * @param {string} [fallbackFormat] - 세션에 dateFormat이 없을 경우 사용할 기본 포맷 (기본값: 'DD MMM YYYY')
 * @returns {string} 포맷된 날짜 문자열 (예: "10 Dec 2025")
 *
 * @example
 * // 설립일 등 시간대 변환 불필요한 날짜
 * const formatted = formatDateOnly('1999-06-01');
 * // 결과: "01 Jun. 1999" (세션의 dateFormat 적용, 시간대 변환 없음)
 */
export const formatDateOnly = (
  dateData: Date | string | number,
  fallbackFormat: string = 'DD MMM YYYY'
): string => {
  if (!dateData) return '';

  // 세션에서 dateFormat 가져오기
  let dateFormat: string = fallbackFormat;
  try {
    const sessionStore = useSessionStore();
    const user = sessionStore.user;
    // 세션의 dateFormat 사용, 없으면 기본값 사용
    dateFormat = user?.user?.dateFormat || fallbackFormat;
  } catch (error) {
    // 세션 스토어를 사용할 수 없는 경우 기본값 사용
    console.warn('Failed to get dateFormat from session, using fallback:', error);
  }

  // 날짜 파싱 (시간대 변환 없이)
  let date: Date;
  if (dateData instanceof Date) {
    date = new Date(dateData);
  } else if (typeof dateData === 'string') {
    // YYYY-MM-DD 형식 확인
    const dateMatch = dateData.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dateMatch) {
      const [, yearStr, monthStr, dayStr] = dateMatch;
      // 로컬 타임존으로 Date 생성 (UTC 오프셋 문제 방지)
      date = new Date(parseInt(yearStr), parseInt(monthStr) - 1, parseInt(dayStr));
    } else {
      // YYYY/MM/DD (슬래시) — 레지스터/연락처 등에서 흔한 표기
      const ymdSlashMatch = dateData.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})/);
      if (ymdSlashMatch) {
        const [, yearStr, monthStr, dayStr] = ymdSlashMatch;
        date = new Date(
          parseInt(yearStr, 10),
          parseInt(monthStr, 10) - 1,
          parseInt(dayStr, 10)
        );
      } else {
      // DD/MM/YYYY 형식 시도
      const ddmmyyyyMatch = dateData.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
      if (ddmmyyyyMatch) {
        const [, dayStr, monthStr, yearStr] = ddmmyyyyMatch;
        date = new Date(parseInt(yearStr), parseInt(monthStr) - 1, parseInt(dayStr));
      } else {
        date = new Date(dateData);
      }
      }
    }
  } else if (typeof dateData === 'number') {
    date = new Date(dateData);
  } else {
    return '';
  }

  // 유효한 날짜인지 확인
  if (isNaN(date.getTime())) {
    return '';
  }

  return parseDocDateFormat(date, dateFormat);
};

/**
 * 세션의 dateFormat을 사용하여 날짜+시간 포맷팅
 * Jurisdiction 시간대 기준으로 변환 후 포맷팅
 * @param {string} dateData - 날짜 데이터 (ISO 문자열, YYYY-MM-DD HH:mm:ss 문자열)
 * @param {string} [fallbackFormat] - 세션에 dateFormat이 없을 경우 사용할 기본 포맷 (기본값: 'DD MMM YYYY')
 * @returns {string} 포맷된 날짜+시간 문자열 (예: "10 Dec 2025 14:30:00")
 *
 * @example
 * // 세션의 dateFormat 사용 (예: 'dd MMM yyyy')
 * const formatted = formatDocDateTime('2025-12-10T14:30:00Z');
 * // 결과: "10 Dec. 2025 14:30:00"
 */
export const formatDocDateTime = (
  dateData: string | null | undefined,
  fallbackFormat: string = 'DD MMM YYYY'
): string => {
  if (!dateData) return '-';

  // 세션에서 dateFormat 및 jurisdiction 가져오기
  let dateFormat: string = fallbackFormat;
  let jurisdiction: string | null = null;
  try {
    const sessionStore = useSessionStore();
    const user = sessionStore.user;
    // 세션의 dateFormat 사용, 없으면 기본값 사용
    dateFormat = user?.user?.dateFormat || fallbackFormat;
    // 세션의 jurisdiction 가져오기
    jurisdiction = user?.customer?.jurisdiction || null;
  } catch (error) {
    // 세션 스토어를 사용할 수 없는 경우 기본값 사용
    console.warn('Failed to get dateFormat/jurisdiction from session, using fallback:', error);
  }

  // Jurisdiction 시간대로 변환 (시간 포함)
  const converted = convertToJurisdictionTimeWithTime(dateData, jurisdiction);
  if (!converted) return dateData;

  const { year, month, day, hours, minutes, seconds } = converted;

  // 날짜 포맷 파싱
  const date = new Date(year, month, day);
  const dateStr = parseDocDateFormat(date, dateFormat);

  // 시간 포함하여 반환
  return `${dateStr} ${hours}:${minutes}:${seconds}`;
};

/**
 * 세션의 dateFormat을 사용하여 날짜 포맷팅 (Composable 버전)
 * Vue 컴포넌트에서 사용하기 편리한 composable 함수
 *
 * @example
 * ```vue
 * <script setup lang="ts">
 * import { useFormatDocDate } from '@/shared/composables/dateUtils';
 *
 * const formatDocDate = useFormatDocDate();
 *
 * const formattedDate = formatDocDate('2025-12-10');
 * // 결과: "10 Dec 2025" (세션의 dateFormat에 따라)
 * </script>
 * ```
 */
export const useFormatDocDate = () => {
  return (dateData: Date | string | number, fallbackFormat: string = 'DD MMM YYYY'): string => {
    return formatDocDate(dateData, fallbackFormat);
  };
};

/**
 * dateFormat을 사용하는 computed 생성기 (read-only)
 * 날짜 표시용 computed를 간단하게 생성
 *
 * @param {() => string | null | undefined} dateGetter - 날짜 값을 반환하는 함수 (YYYY-MM-DD 형식)
 * @returns {import('vue').ComputedRef<string>} 포맷된 날짜를 반환하는 computed
 *
 * @example
 * ```vue
 * <script setup lang="ts">
 * import { ref } from 'vue';
 * import { useDocDateDisplay } from '@/shared/composables/dateUtils';
 *
 * const dateOfBirth = ref('1990-05-15');
 * const dateOfBirthDisplay = useDocDateDisplay(() => dateOfBirth.value);
 * // dateOfBirthDisplay.value: "15 May 1990"
 * </script>
 * ```
 */
export const useDocDateDisplay = (dateGetter: () => string | null | undefined) => {
  return computed(() => {
    const dateVal = dateGetter();
    if (!dateVal) return '';
    return formatDocDate(dateVal);
  });
};

/**
 * UTC 날짜/시간을 브라우저 로컬 시간대로 변환하여 포맷팅
 * @param {string} dateData - 날짜 데이터 (ISO 문자열, YYYY-MM-DD HH:mm:ss 문자열)
 * @param {string} [fallbackFormat] - 세션에 dateFormat이 없을 경우 사용할 기본 포맷 (기본값: 'DD MMM YYYY')
 * @returns {string} 포맷된 날짜+시간 문자열 (예: "10 Dec 2025 14:30:00")
 *
 * @example
 * // 브라우저 로컬 시간대로 변환
 * const formatted = formatLocalDateTime('2025-12-10T05:30:00Z');
 * // 결과 (한국): "10 Dec. 2025 14:30:00"
 */
export const formatLocalDateTime = (
  dateData: string | null | undefined,
  fallbackFormat: string = 'DD MMM YYYY'
): string => {
  if (!dateData) return '-';

  // 세션에서 dateFormat 가져오기
  let dateFormat: string = fallbackFormat;
  try {
    const sessionStore = useSessionStore();
    const user = sessionStore.user;
    dateFormat = user?.user?.dateFormat || fallbackFormat;
  } catch (error) {
    // 세션 스토어를 사용할 수 없는 경우 기본값 사용
  }

  try {
    // DB에서 받은 날짜 문자열을 UTC로 인식하도록 처리
    let normalizedDateValue = dateData.trim();
    normalizedDateValue = normalizedDateValue.replace(' ', 'T');

    // 타임존 표시가 없으면 'Z' (UTC) 추가
    if (!normalizedDateValue.endsWith('Z') &&
        !normalizedDateValue.includes('+') &&
        !normalizedDateValue.match(/[-]\d{2}:\d{2}$/)) {
      normalizedDateValue += 'Z';
    }

    const date = new Date(normalizedDateValue);
    if (isNaN(date.getTime())) return dateData;

    // 브라우저 로컬 시간대로 자동 변환됨 (Date 객체의 기본 동작)
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    // 날짜 포맷 파싱
    const dateObj = new Date(year, month, day);
    const dateStr = parseDocDateFormat(dateObj, dateFormat);

    return `${dateStr} ${hours}:${minutes}:${seconds}`;
  } catch (error) {
    return dateData;
  }
};

// ========================================
// 날짜 함수 별칭 (하위 호환성)
// ========================================
// formatDateByJurisdiction -> formatDocDate 와 동일
export const formatDateByJurisdiction = formatDocDate;
// formatDateTimeByJurisdiction -> formatLocalDateTime 사용 (브라우저 시간대)
export const formatDateTimeByJurisdiction = formatLocalDateTime;

export default {
  getTimezoneFromLanguage,
  getTimezoneFromJurisdiction,
  getTimezoneByJurisdiction,
  getCurrentDateByJurisdiction,
  formatDateByLanguage,
  useFormatDate,
  formatDateByUserFormat,
  useFormatDateByUserFormat,
  useDateField,
  formatDocDate,
  formatDocDateTime,
  normalizeDateInputToIso,
  formatDateByJurisdiction,
  formatDateTimeByJurisdiction,
  useFormatDocDate,
  useDocDateDisplay
};

